import React from 'react';
import type { EChartsOption } from 'echarts';
import ReactECharts from 'components/ThemedECharts';

import {
  DARK_BLUE, MEDIUM_BLUE, BLUE, LIGHT_BLUE, GREEN, EXTRA_STATE, MUTED,
} from 'constants/colors';
import GraficoTelaCheia from 'components/Relatorios/shared/Mobile/GraficoTelaCheia';
import { comFormatadores, criarValueFormatter } from 'helper/Relatorios/Dessem/chartOption';
import { DessemChartOption, DessemSerie } from 'types/Relatorios/Dessem';

/**
 * A 1ª série (o D+1, linha cheia) tem cor exclusiva; as seguintes ciclam numa paleta sem ela.
 * A Geração Renovável do informe de sexta tem 7 séries (sábado até a sexta seguinte): com uma
 * paleta única de 6 cores, a 7ª repetia a cor do D+1.
 */
// tema: dados-inicio — cor de série (o ThemedECharts adapta no escuro)
export const COR_PRIMEIRA_SERIE = DARK_BLUE;
export const PALETA_SEGUINTES = [MEDIUM_BLUE, BLUE, LIGHT_BLUE, GREEN, EXTRA_STATE, MUTED];
// tema: dados-fim

export const corDaSerie = (indice: number): string => (
  indice === 0 ? COR_PRIMEIRA_SERIE : PALETA_SEGUINTES[(indice - 1) % PALETA_SEGUINTES.length]
);

interface Props {
  periodos: string[];
  series: DessemSerie[];
  /** Destaca o ponto de máximo da primeira série (markPoint), conforme Figma (Carga). */
  destacarPico?: boolean;
  /** Label do eixo Y (ex.: "MW", "Geração (MWm)"), conforme Figma. */
  nomeEixoY?: string;
  /**
   * Eixo Y ajustado aos dados, sem começar no zero (Carga e Carga Líquida, como no relatório do
   * Planejamento; 14/10/2026): a carga do SIN varia entre ~70 e ~100 mil MW e, com o eixo a partir
   * do zero, a curva ficava achatada. A Geração Renovável segue no zero (a solar zera à noite).
   */
  eixoYAjustado?: boolean;
  /**
   * `option` montado pelo backend (contrato §2.14). Vai para o gráfico como veio, só com o
   * `formatter` do eixo Y e o `valueFormatter` do tooltip; sem ele (ausente ou `null`), o gráfico
   * é montado aqui pelas props acima, como antes.
   */
  option?: DessemChartOption;
}

/** Em eixos de meia em meia hora (`HH:30`), mostra só as horas cheias, como o relatório. */
export const soHorasCheias = (_indice: number, valor: string): boolean => !valor.endsWith(':30');

export const formatarMW = (valor: number): string => valor.toLocaleString('pt-BR', {
  maximumFractionDigits: 0,
});

/** Tooltip em pt-BR sem casas, com o `option` do BE e no gráfico montado aqui. */
const TOOLTIP_MW = criarValueFormatter(formatarMW);

/**
 * Rótulo do pico no gráfico montado aqui, em pt-BR ("98.850"). No escopo do módulo, como toda
 * função do `option`: com a mesma referência entre renders, o `echarts-for-react` vê o `option`
 * igual e não chama o `setOption`, que com `notMerge` recriaria o gráfico e religaria na legenda a
 * série que o usuário desligou.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rotuloDoPico = (p: any): string => formatarMW(Number(p.value));

/** Passo "redondo" do eixo: metade da potência de 10 da amplitude (25 mil MW -> 5 mil). */
const passoRedondo = (amplitude: number): number => (
  amplitude > 0 ? 10 ** Math.floor(Math.log10(amplitude)) / 2 : 1
);

/**
 * Eixo Y ajustado (`eixoYAjustado`), calculado dos dados: folga de 30% da amplitude abaixo e 20%
 * acima (espaço para o rótulo do pico), arredondada ao passo, e marcas de passo em passo — como o
 * relatório do Planejamento. Carga de 14/10/2026 (73,9 a 98,8 mil MW) -> 65 a 105 mil, de 5 em 5
 * mil; Carga Líquida (35 a 72 mil) -> 20 a 80 mil, a mesma escala do relatório. Nunca abaixo de
 * zero. `null` (o ECharts decide) sem dados ou com série constante.
 */
export const limitesEixoY = (
  series: DessemSerie[],
): { min: number; max: number; interval: number } | null => {
  const valores = series.flatMap((s) => s.valores).filter((v) => Number.isFinite(v));
  if (valores.length === 0) return null;
  const minimo = Math.min(...valores);
  const maximo = Math.max(...valores);
  const amplitude = maximo - minimo;
  if (amplitude <= 0) return null;
  const passo = passoRedondo(amplitude);
  return {
    min: Math.max(0, Math.floor((minimo - amplitude * 0.3) / passo) * passo),
    max: Math.ceil((maximo + amplitude * 0.2) / passo) * passo,
    interval: passo,
  };
};

export default function DessemSerieChart({
  periodos, series, destacarPico, nomeEixoY, eixoYAjustado, option: optionDoBe,
}: Props) {
  const optionLocal = (): EChartsOption => ({
    legend: { data: series.map((s) => s.nome) },
    // Horas cheias na vertical, como no relatório: as 24 cabem a partir de ~350 px de largura.
    // `interval` como função desliga o anti-sobreposição automático do ECharts; o `hideOverlap`
    // fica de rede de segurança para tela muito estreita (some parte das horas, nunca embola).
    xAxis: {
      type: 'category',
      data: periodos,
      axisLabel: { interval: soHorasCheias, hideOverlap: true, rotate: 90 },
    },
    yAxis: {
      type: 'value',
      name: nomeEixoY,
      nameLocation: 'middle',
      nameGap: 60,
      nameRotate: 90,
      // Função do escopo do módulo, não criada no render: ver `rotuloDoPico`.
      axisLabel: { formatter: formatarMW },
      ...(eixoYAjustado ? (limitesEixoY(series) ?? {}) : {}),
    },
    series: series.map((s, i) => ({
      name: s.nome,
      type: 'line',
      data: s.valores,
      color: corDaSerie(i),
      ...(s.tracejado ? { lineStyle: { type: 'dashed' } } : {}),
      ...(destacarPico && i === 0 ? {
        markPoint: {
          data: [{ type: 'max', name: 'Máximo' }],
          // Valor acima do marcador e em pt-BR ("98.850"): dentro do pino o número não cabia.
          label: { position: 'top', formatter: rotuloDoPico },
        },
      } : {}),
    })),
    tooltip: { trigger: 'axis' },
    grid: { left: '5%', right: '5%', containLabel: true },
  });

  // O gráfico montado aqui já formata o eixo Y; o do BE ganha o formatter (função não vem no JSON).
  const option = optionDoBe
    ? comFormatadores(optionDoBe, { tooltip: TOOLTIP_MW, eixoY: formatarMW })
    : comFormatadores(optionLocal(), { tooltip: TOOLTIP_MW });

  return (
    <GraficoTelaCheia>
      {(telaCheia) => (
        <ReactECharts
          option={option}
          // Trocar entre o `option` do BE e o montado aqui não pode deixar chave velha no gráfico.
          notMerge
          style={telaCheia ? { height: '100%', width: '100%' } : { height: 360 }}
        />
      )}
    </GraficoTelaCheia>
  );
}
